import React, { useState, useEffect } from 'react';
import {
    Save,
    FolderOpen,
    Search,
    Filter,
    Trash2,
    Edit3,
    Download,
    Upload,
    Calendar,
    MapPin,
    Truck,
    Clock,
    BarChart3,
    Eye,
    Copy,
    AlertCircle,
    CheckCircle,
    Plus,
    Settings,
    Grid3x3,
    List,
    Navigation,
    RefreshCw
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import {
    getSavedMissions,
    deleteMission,
    updateMissionMetadata,
    searchMissions,
    getMissionsByStatus,
    getMissionStatistics,
    exportMission,
    importMission,
    getStorageInfo
} from '../utils/missionStorageAPI';
import MissionAnalytics from './MissionAnalytics';
import MissionCompletionModal from './MissionCompletionModal';

const SavedMissions = ({ onLoadMission, onNewMission, currentMissionId, onVehicleUpdated }) => {
    const [missions, setMissions] = useState([]);
    const [filteredMissions, setFilteredMissions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [selectedMissions, setSelectedMissions] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [editingMission, setEditingMission] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [storageInfo, setStorageInfo] = useState(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [viewMode, setViewMode] = useState('tiles'); // 'tiles' or 'list'
    const [isLoading, setIsLoading] = useState(true); // Loading state
    
    // Mission completion modal state
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completedMissionData, setCompletedMissionData] = useState(null);
    const [completionData, setCompletionData] = useState(null);

    const { getThemeClasses, theme } = useTheme();
    const themeClasses = getThemeClasses();

    // Load missions on component mount (parallel for better performance)
    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            loadMissions(),
            loadStatistics(),
            loadStorageInfo()
        ]).catch(error => {
            console.error('Failed to load mission data:', error);
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    // Filter and sort missions when dependencies change
    useEffect(() => {
        filterAndSortMissions();
    }, [missions, searchQuery, statusFilter, sortBy, sortOrder]);

    const loadMissions = async () => {
        const savedMissions = await getSavedMissions();
        setMissions(savedMissions);
        
        // Calculate statistics and storage info from loaded missions (no additional API calls)
        const stats = getMissionStatistics(savedMissions);
        setStatistics(stats);
        
        const info = getStorageInfo(savedMissions);
        setStorageInfo(info);
    };

    const loadStatistics = async () => {
        // Deprecated: Now calculated from loaded missions in loadMissions()
        // Kept for backward compatibility but does nothing
    };

    const loadStorageInfo = async () => {
        // Deprecated: Now calculated from loaded missions in loadMissions()
        // Kept for backward compatibility but does nothing
    };

    const filterAndSortMissions = async () => {
        let filtered = [...missions];

        // Apply search filter
        if (searchQuery.trim()) {
            filtered = await searchMissions(searchQuery);
        }

        // Apply status filter
        if (statusFilter !== 'all') {
            filtered = filtered.filter(m => m.status === statusFilter);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aValue, bValue;

            switch (sortBy) {
                case 'name':
                    aValue = a.missionInfo.name.toLowerCase();
                    bValue = b.missionInfo.name.toLowerCase();
                    break;
                case 'createdAt':
                    aValue = new Date(a.createdAt);
                    bValue = new Date(b.createdAt);
                    break;
                case 'updatedAt':
                    aValue = new Date(a.updatedAt);
                    bValue = new Date(b.updatedAt);
                    break;
                case 'complexity':
                    aValue = a.summary.complexityScore;
                    bValue = b.summary.complexityScore;
                    break;
                case 'distance':
                    aValue = parseFloat(a.summary.totalDistance);
                    bValue = parseFloat(b.summary.totalDistance);
                    break;
                default:
                    aValue = a.createdAt;
                    bValue = b.createdAt;
            }

            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredMissions(filtered);
    };

    const handleDeleteMission = async (missionId) => {
        const result = await deleteMission(missionId);
        if (result.success) {
            loadMissions();
            loadStatistics();
            loadStorageInfo();
            setShowDeleteConfirm(null);
            setSelectedMissions(prev => prev.filter(id => id !== missionId));
        } else {
            alert('Failed to delete mission: ' + result.error);
        }
    };

    const handleUpdateMission = async (missionId, updates) => {
        const result = await updateMissionMetadata(missionId, updates);
        if (result.success) {
            loadMissions();
            setEditingMission(null);
        } else {
            alert('Failed to update mission: ' + result.error);
        }
    };

    const handleExportMission = async (missionId) => {
        const result = await exportMission(missionId);
        if (result.success) {
            const url = URL.createObjectURL(result.blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = result.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } else {
            alert('Failed to export mission: ' + result.error);
        }
    };

    const handleImportMission = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const result = await importMission(e.target.result);
                if (result.success) {
                    loadMissions();
                    loadStatistics();
                    loadStorageInfo();
                    setShowImportDialog(false);
                    alert('Mission imported successfully!');
                } else {
                    alert('Failed to import mission: ' + result.error);
                }
            } catch (error) {
                alert('Failed to read mission file');
            }
        };
        reader.readAsText(file);
    };

    const getComplexityColor = (level) => {
        switch (level.toLowerCase()) {
            case 'low': return 'text-green-500';
            case 'moderate': return 'text-yellow-500';
            case 'high': return 'text-orange-500';
            case 'very high': return 'text-red-500';
            default: return themeClasses.textMuted;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'planned': return 'bg-gray-500/20 text-gray-400';
            case 'applied': return 'bg-green-500/20 text-green-400';
            case 'in-progress': return 'bg-blue-500/20 text-blue-400';
            case 'completed': return 'bg-purple-500/20 text-purple-400';
            case 'cancelled': return 'bg-red-500/20 text-red-400';
            default: return 'bg-gray-500/20 text-gray-400';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Apply to Vehicle handler removed - simplified workflow
    // Old apply to vehicle workflow has been removed
    // New simplified workflow uses handleStartMission below

    // Conflict handlers removed - no longer needed in simplified workflow

    // Handle Start Mission (planned -> in-progress)
    const handleStartMission = async (mission) => {
        console.log('▶️ Starting mission:', mission.missionInfo.name);
        
        const { startMission } = await import('../utils/missionStorageAPI');
        
        // Use vehicle.id directly (e.g., "v1053") instead of extracting from uniqueId
        const vehicleId = mission.missionInfo.vehicleInfo?.id;
        const vehicleHealth = mission.missionInfo.vehicleInfo?.health;
        const vehicleInfo = mission.missionInfo.vehicleInfo;
        
        console.log('🚗 Vehicle ID for mission start:', vehicleId);
        console.log('🏥 Vehicle Health:', vehicleHealth);
        
        const result = await startMission(mission.id, vehicleId, vehicleHealth, vehicleInfo);
        
        if (result.success) {
            console.log('✅ Mission started successfully');
            console.log('   Backend automatically added mission to vehicle history');
            
            loadMissions();
            
            // Dispatch event to notify VehicleMissionHistory component
            window.dispatchEvent(new CustomEvent('missionStatusChanged'));
            
            // Trigger fleet data refresh in parent component
            if (onVehicleUpdated) {
                console.log('🔄 Triggering fleet data refresh...');
                onVehicleUpdated();
            }
            
            alert(`✅ Mission Started!\n\n` +
                  `Mission: ${mission.missionInfo.name}\n` +
                  `Vehicle: ${mission.missionInfo.vehicleInfo?.brand} ${mission.missionInfo.vehicleInfo?.model}\n` +
                  `Status: IN PROGRESS\n\n` +
                  `You can now reroute this mission if needed.`);
        } else {
            alert(`Failed to start mission:\n\n${result.error}`);
        }
    };

    // Handle Complete Mission (in-progress -> completed)
    const handleCompleteMission = async (mission) => {
        console.log('✅ Completing mission:', mission.missionInfo.name);
        
        const { completeMission } = await import('../utils/missionStorageAPI');
        
        // Simplified: Backend uses predicted stats from terrain analysis
        // No location or health parameters needed
        const result = await completeMission(mission.id);
        
        if (result.success) {
            console.log('✅ Mission completed successfully');
            console.log('📊 Completion data:', result.completionData);
            
            // Reload missions to show updated status
            loadMissions();
            
            // Dispatch event to notify VehicleMissionHistory component
            window.dispatchEvent(new CustomEvent('missionStatusChanged'));
            
            // Trigger fleet data refresh in parent component
            if (onVehicleUpdated) {
                console.log('🔄 Triggering fleet data refresh...');
                onVehicleUpdated();
            }
            
            // Show completion modal with results
            setCompletedMissionData(mission);
            setCompletionData(result.completionData);
            setShowCompletionModal(true);
        } else {
            console.error('❌ Failed to complete mission:', result.error);
            alert(`Failed to complete mission:\n\n${result.error}`);
        }
    };

    // Reroute handlers removed - simplified workflow

    return (
        <div className="p-3 sm:p-4 md:p-6 overflow-x-hidden">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
                <div>
                    <h2 className={`text-2xl sm:text-3xl font-bold ${themeClasses.textPrimary}`}>Saved Missions</h2>
                    <p className={`${themeClasses.textMuted} mt-1 text-sm sm:text-base`}>Manage and load your analyzed missions</p>
                </div>
                <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                    <button
                        onClick={() => setShowImportDialog(true)}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Upload size={18} />
                        Import
                    </button>
                    <button
                        onClick={onNewMission}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                    >
                        <Plus size={18} />
                        New Mission
                    </button>
                </div>
            </div>

            {/* Statistics Cards */}
            {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4`}>
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-600 p-2 rounded-lg">
                                <FolderOpen className="text-white" size={18} />
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-sm`}>Total Missions</p>
                                <p className={`${themeClasses.textPrimary} font-semibold text-xl`}>{statistics.total}</p>
                            </div>
                        </div>
                    </div>

                    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4`}>
                        <div className="flex items-center gap-3">
                            <div className="bg-green-600 p-2 rounded-lg">
                                <MapPin className="text-white" size={18} />
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-sm`}>Total Distance</p>
                                <p className={`${themeClasses.textPrimary} font-semibold text-xl`}>{statistics.totalDistance.toFixed(1)} km</p>
                            </div>
                        </div>
                    </div>

                    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4`}>
                        <div className="flex items-center gap-3">
                            <div className="bg-blue-600 p-2 rounded-lg">
                                <Truck className="text-white" size={18} />
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-sm`}>Total Fuel</p>
                                <p className={`${themeClasses.textPrimary} font-semibold text-xl`}>{statistics.totalFuel.toFixed(1)} L</p>
                            </div>
                        </div>
                    </div>

                    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-4`}>
                        <div className="flex items-center gap-3">
                            <div className="bg-purple-600 p-2 rounded-lg">
                                <BarChart3 className="text-white" size={18} />
                            </div>
                            <div>
                                <p className={`${themeClasses.textMuted} text-sm`}>Avg Complexity</p>
                                <p className={`${themeClasses.textPrimary} font-semibold text-xl`}>{statistics.averageComplexity}/100</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-4 mb-6`}>
                <div className="flex flex-wrap items-center gap-4">
                    {/* Search */}
                    <div className="flex-1 min-w-64">
                        <div className="relative">
                            <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.textMuted}`} size={18} />
                            <input
                                type="text"
                                placeholder="Search missions..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`w-full pl-10 pr-4 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
                            />
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                        <Filter className={themeClasses.textMuted} size={18} />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className={`px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                        >
                            <option value="all">All Status</option>
                            <option value="planned">Planned</option>
                            <option value="applied">Applied</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>

                    {/* Sort Options */}
                    <div className="flex items-center gap-2">
                        <span className={`${themeClasses.textMuted} text-sm`}>Sort by:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className={`px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                        >
                            <option value="createdAt">Created Date</option>
                            <option value="updatedAt">Updated Date</option>
                            <option value="name">Name</option>
                            <option value="complexity">Complexity</option>
                            <option value="distance">Distance</option>
                        </select>
                        <button
                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className={`px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} hover:${themeClasses.secondary} transition-colors`}
                        >
                            {sortOrder === 'asc' ? '↑' : '↓'}
                        </button>
                    </div>

                    {/* View Toggle */}
                    <div className="flex items-center gap-2 ml-auto">
                        <span className={`${themeClasses.textMuted} text-sm`}>View:</span>
                        <div className="flex gap-1 border ${themeClasses.border} rounded-lg p-1">
                            <button
                                onClick={() => setViewMode('tiles')}
                                className={`p-2 rounded transition-colors ${
                                    viewMode === 'tiles'
                                        ? 'bg-blue-600 text-white'
                                        : `${themeClasses.textMuted} hover:${themeClasses.secondary}`
                                }`}
                                title="Tile View"
                            >
                                <Grid3x3 size={18} />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded transition-colors ${
                                    viewMode === 'list'
                                        ? 'bg-blue-600 text-white'
                                        : `${themeClasses.textMuted} hover:${themeClasses.secondary}`
                                }`}
                                title="List View"
                            >
                                <List size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Missions List/Grid */}
            <div className={viewMode === 'tiles' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
                {isLoading ? (
                    // Loading skeleton
                    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-8 text-center ${viewMode === 'tiles' ? 'col-span-full' : ''}`}>
                        <RefreshCw className={`mx-auto ${themeClasses.textMuted} mb-4 animate-spin`} size={48} />
                        <h3 className={`${themeClasses.textPrimary} font-semibold mb-2`}>Loading missions...</h3>
                        <p className={`${themeClasses.textMuted}`}>Please wait while we fetch your saved missions</p>
                    </div>
                ) : filteredMissions.length === 0 ? (
                    <div className={`${themeClasses.card} border ${themeClasses.border} rounded-xl p-8 text-center ${viewMode === 'tiles' ? 'col-span-full' : ''}`}>
                        <FolderOpen className={`mx-auto ${themeClasses.textMuted} mb-4`} size={48} />
                        <h3 className={`${themeClasses.textPrimary} font-semibold mb-2`}>No missions found</h3>
                        <p className={`${themeClasses.textMuted} mb-4`}>
                            {searchQuery || statusFilter !== 'all'
                                ? 'Try adjusting your search or filter criteria'
                                : 'Start by creating your first mission'
                            }
                        </p>
                        {!searchQuery && statusFilter === 'all' && (
                            <button
                                onClick={onNewMission}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                            >
                                Create New Mission
                            </button>
                        )}
                    </div>
                ) : (
                    filteredMissions.map((mission) => (
                        <MissionCard
                            key={mission.id}
                            mission={mission}
                            isCurrentMission={mission.id === currentMissionId}
                            onLoad={() => onLoadMission(mission)}
                            onDelete={() => setShowDeleteConfirm(mission.id)}
                            onEdit={() => setEditingMission(mission)}
                            onExport={() => handleExportMission(mission.id)}
                            onStartMission={handleStartMission}
                            onCompleteMission={handleCompleteMission}
                            getComplexityColor={getComplexityColor}
                            getStatusColor={getStatusColor}
                            formatDate={formatDate}
                            themeClasses={themeClasses}
                            viewMode={viewMode}
                        />
                    ))
                )}
            </div>

            {/* Storage Info */}
            {storageInfo && (
                <div className={`mt-6 ${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-4`}>
                    <div className={`flex items-center justify-between text-sm ${themeClasses.textMuted}`}>
                        <span>Storage: {storageInfo.storageSizeFormatted} used</span>
                        <span>{storageInfo.missionCount} missions saved</span>
                        {storageInfo.lastUpdated && (
                            <span>Last updated: {formatDate(storageInfo.lastUpdated)}</span>
                        )}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border ${themeClasses.border} rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
                        <div className="flex items-center gap-3 mb-4">
                            <AlertCircle className="text-red-400" size={24} />
                            <h3 className={`${themeClasses.textPrimary} font-semibold`}>Delete Mission</h3>
                        </div>
                        <p className={`${themeClasses.textSecondary} mb-6`}>
                            Are you sure you want to delete this mission? This action cannot be undone.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => handleDeleteMission(showDeleteConfirm)}
                                className={`flex-1 px-4 py-2 ${themeClasses.buttonDanger} rounded-lg transition-colors`}
                            >
                                Delete
                            </button>
                            <button
                                onClick={() => setShowDeleteConfirm(null)}
                                className={`flex-1 px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Import Dialog */}
            {showImportDialog && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border ${themeClasses.border} rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
                        <div className="flex items-center gap-3 mb-4">
                            <Upload className="text-blue-400" size={24} />
                            <h3 className={`${themeClasses.textPrimary} font-semibold`}>Import Mission</h3>
                        </div>
                        <p className={`${themeClasses.textSecondary} mb-4`}>
                            Select a mission JSON file to import.
                        </p>
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleImportMission}
                            className={`w-full mb-4 ${themeClasses.textPrimary}`}
                        />
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowImportDialog(false)}
                                className={`flex-1 px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mission Completion Modal */}
            {showCompletionModal && completedMissionData && completionData && (
                <MissionCompletionModal
                    mission={completedMissionData}
                    completionData={completionData}
                    onClose={() => {
                        setShowCompletionModal(false);
                        setCompletedMissionData(null);
                        setCompletionData(null);
                    }}
                />
            )}

            {/* Edit Mission Dialog */}
            {editingMission && (
                <EditMissionDialog
                    mission={editingMission}
                    onSave={(updates) => handleUpdateMission(editingMission.id, updates)}
                    onCancel={() => setEditingMission(null)}
                    themeClasses={themeClasses}
                />
            )}


        </div>
    );
};

// Mission Card Component
const MissionCard = ({
    mission,
    isCurrentMission,
    onLoad,
    onDelete,
    onEdit,
    onExport,
    onStartMission,
    onCompleteMission,
    getComplexityColor,
    getStatusColor,
    formatDate,
    themeClasses,
    viewMode = 'tiles'
}) => {
    // List view - compact layout with route info
    if (viewMode === 'list') {
        return (
            <div className={`${themeClasses.card} border rounded-xl transition-all hover:${themeClasses.secondary} ${isCurrentMission ? 'border-blue-500/50 bg-blue-500/10' : themeClasses.border}`}>
                <div className="flex items-center gap-3 p-3">
                    {/* Mission Info Section */}
                    <div className="flex-1 min-w-0">
                        {/* Title and Badges Row */}
                        <div className="flex items-center gap-2 mb-1.5">
                            <h3 className={`${themeClasses.textPrimary} font-semibold text-base truncate`}>
                                {mission.missionInfo.name}
                            </h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(mission.status)}`}>
                                {mission.status}
                            </span>
                            {isCurrentMission && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-300 whitespace-nowrap">
                                    Current
                                </span>
                            )}
                        </div>

                        {/* Route Information */}
                        <div className={`flex items-center gap-1.5 mb-2 text-xs ${themeClasses.textMuted}`}>
                            <MapPin size={12} className="flex-shrink-0" />
                            <span className="truncate">
                                {mission.locationData.startLocationName || 'Start'} → {mission.locationData.destinationLocationName || 'Destination'}
                            </span>
                        </div>

                        {/* Compact Metadata Row */}
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                            <span className={`flex items-center gap-1 ${themeClasses.textMuted}`}>
                                <Calendar size={11} className="flex-shrink-0" />
                                {formatDate(mission.createdAt)}
                            </span>
                            <span className={`${themeClasses.textMuted}`}>
                                {mission.summary.totalDistance}
                            </span>
                            <span className={`${themeClasses.textMuted}`}>
                                {mission.summary.estimatedDuration}
                            </span>
                            {mission.missionInfo.vehicleInfo && (
                                <>
                                    <span className={`flex items-center gap-1 ${themeClasses.textMuted}`}>
                                        <Truck size={11} className="flex-shrink-0" />
                                        {mission.missionInfo.vehicleInfo.brand} {mission.missionInfo.vehicleInfo.model}
                                    </span>
                                    {mission.missionInfo.vehicleInfo.health !== undefined && (
                                        <span className="flex items-center gap-1">
                                            <span className={`font-semibold ${
                                                mission.missionInfo.vehicleInfo.health >= 80 ? 'text-green-400' :
                                                mission.missionInfo.vehicleInfo.health >= 60 ? 'text-yellow-400' :
                                                mission.missionInfo.vehicleInfo.health >= 40 ? 'text-orange-400' :
                                                'text-red-400'
                                            }`}>
                                                {mission.missionInfo.vehicleInfo.health}%
                                            </span>
                                            <span className="text-gray-500">→</span>
                                            <span className={`font-semibold ${
                                                mission.predictedHealth !== undefined ? (
                                                    mission.predictedHealth >= 80 ? 'text-green-400' :
                                                    mission.predictedHealth >= 60 ? 'text-yellow-400' :
                                                    mission.predictedHealth >= 40 ? 'text-orange-400' :
                                                    'text-red-400'
                                                ) : 'text-gray-500'
                                            }`}>
                                                {mission.predictedHealth !== undefined ? `${mission.predictedHealth}%` : 'N/A'}
                                            </span>
                                        </span>
                                    )}
                                </>
                            )}
                            <span className={`font-medium ${getComplexityColor(mission.summary.complexityLevel)}`}>
                                {mission.summary.complexityLevel}
                            </span>
                        </div>
                    </div>
                    
                    {/* Actions Section - Compact */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        {/* Start Mission Button for Planned Missions */}
                        {mission.status === 'planned' && onStartMission && (
                            <button
                                onClick={() => onStartMission(mission)}
                                className="px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center gap-1 text-xs font-medium"
                                title="Start Mission"
                            >
                                <Navigation size={13} />
                                <span className="hidden lg:inline">Start</span>
                            </button>
                        )}
                        
                        {/* Reroute Button for In-Progress Missions */}
                        {mission.status === 'in-progress' && onCompleteMission && (
                            <button
                                onClick={() => onCompleteMission(mission)}
                                className="px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center gap-1 text-xs font-medium"
                                title="Complete Mission"
                            >
                                <CheckCircle size={13} />
                                <span className="hidden lg:inline">Complete</span>
                            </button>
                        )}
                        
                        {/* Load Button */}
                        <button
                            onClick={onLoad}
                            className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center gap-1 text-xs"
                            title="Load Mission"
                        >
                            <Eye size={13} />
                            <span className="hidden lg:inline">Load</span>
                        </button>

                        {/* Icon Actions */}
                        <button
                            onClick={onEdit}
                            className={`p-1.5 ${themeClasses.buttonSecondary} rounded transition-colors`}
                            title="Edit Mission"
                        >
                            <Edit3 size={13} />
                        </button>
                        <button
                            onClick={onExport}
                            className="p-1.5 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                            title="Export Mission"
                        >
                            <Download size={13} />
                        </button>
                        <button
                            onClick={onDelete}
                            className={`p-1.5 ${themeClasses.buttonDanger} rounded transition-colors`}
                            title="Delete Mission"
                        >
                            <Trash2 size={13} />
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    // Tile view - improved card layout
    return (
        <div className={`${themeClasses.card} border rounded-xl transition-all hover:${themeClasses.secondary} ${isCurrentMission ? 'border-blue-500/50 bg-blue-500/10' : themeClasses.border} flex flex-col h-full`}>
            {/* Header Section */}
            <div className="p-4 border-b border-gray-700/50">
                <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className={`${themeClasses.textPrimary} font-semibold text-base flex-1 line-clamp-2`}>
                        {mission.missionInfo.name}
                    </h3>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStatusColor(mission.status)}`}>
                            {mission.status}
                        </span>
                        {isCurrentMission && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-300 whitespace-nowrap">
                                Current
                            </span>
                        )}
                    </div>
                </div>

                {/* Mission Metadata */}
                <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-xs ${themeClasses.textMuted}`}>
                    <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(mission.createdAt)}
                    </span>
                    <span className="flex items-center gap-1">
                        <MapPin size={12} />
                        {mission.summary.totalDistance}
                    </span>
                    <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {mission.summary.estimatedDuration}
                    </span>
                    <span className={`font-medium ${getComplexityColor(mission.summary.complexityLevel)}`}>
                        {mission.summary.complexityLevel}
                    </span>
                </div>
            </div>

            {/* Content Section */}
            <div className="p-4 flex-1">
                {/* Vehicle Information */}
                {mission.missionInfo.vehicleInfo && (
                    <div className={`${themeClasses.secondary} rounded-lg p-2.5 mb-3`}>
                        <div className="flex items-center gap-2 mb-2">
                            <Truck size={14} className="text-blue-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <span className={`text-sm ${themeClasses.textPrimary} font-medium block truncate`}>
                                    {mission.missionInfo.vehicleInfo.brand} {mission.missionInfo.vehicleInfo.model}
                                </span>
                                <span className={`text-xs ${themeClasses.textMuted}`}>
                                    ID: {mission.missionInfo.vehicleInfo.id || 'N/A'}
                                </span>
                            </div>
                        </div>
                        
                        {/* Health Information - Before and After */}
                        {mission.missionInfo.vehicleInfo.health !== undefined && (
                            <div className="grid grid-cols-2 gap-3 text-xs mt-2 pt-2 border-t border-gray-700/30">
                                <div>
                                    <div className={`${themeClasses.textMuted} mb-1 text-[11px]`}>Current Health</div>
                                    <div className={`text-lg font-bold ${
                                        mission.missionInfo.vehicleInfo.health >= 80 ? 'text-green-400' :
                                        mission.missionInfo.vehicleInfo.health >= 60 ? 'text-yellow-400' :
                                        mission.missionInfo.vehicleInfo.health >= 40 ? 'text-orange-400' :
                                        'text-red-400'
                                    }`}>
                                        {mission.missionInfo.vehicleInfo.health}%
                                    </div>
                                </div>
                                <div>
                                    <div className={`${themeClasses.textMuted} mb-1 text-[11px]`}>Post-Mission Health</div>
                                    <div className={`text-lg font-bold ${
                                        mission.predictedHealth !== undefined ? (
                                            mission.predictedHealth >= 80 ? 'text-green-400' :
                                            mission.predictedHealth >= 60 ? 'text-yellow-400' :
                                            mission.predictedHealth >= 40 ? 'text-orange-400' :
                                            'text-red-400'
                                        ) : 'text-gray-500'
                                    }`}>
                                        {mission.predictedHealth !== undefined ? `${mission.predictedHealth}%` : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Show completion percentage for in-progress missions */}
                        {mission.status === 'in-progress' && mission.completionPercentage !== undefined && (
                            <div className="mt-2 pt-2 border-t border-gray-700/30">
                                <div className={`${themeClasses.textMuted} mb-1 text-[11px]`}>Mission Progress</div>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-700 rounded-full h-2">
                                        <div 
                                            className="bg-blue-500 h-2 rounded-full transition-all"
                                            style={{ width: `${mission.completionPercentage}%` }}
                                        />
                                    </div>
                                    <span className={`text-sm font-bold ${themeClasses.textPrimary}`}>
                                        {mission.completionPercentage}%
                                    </span>
                                </div>
                            </div>
                        )}
                        
                        {/* Show reroute count if mission has been rerouted */}
                        {mission.reroutes && mission.reroutes.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-700/30">
                                <div className={`text-xs ${themeClasses.textMuted} flex items-center gap-1`}>
                                    <RefreshCw size={11} />
                                    <span>Rerouted {mission.reroutes.length} time{mission.reroutes.length > 1 ? 's' : ''}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {mission.notes && (
                    <p className={`${themeClasses.textSecondary} text-sm line-clamp-2`}>{mission.notes}</p>
                )}

                {/* Mission Analytics for Completed Missions */}
                {mission.status === 'completed' && <MissionAnalytics mission={mission} />}
            </div>

            {/* Actions Section */}
            <div className="p-4 pt-0 mt-auto">
                {/* Start Mission Button for Planned Missions - Full Width */}
                {mission.status === 'planned' && onStartMission && (
                    <button
                        onClick={() => onStartMission(mission)}
                        className="w-full mb-2 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm font-medium"
                        title="Start Mission"
                    >
                        <Navigation size={14} />
                        Start Mission
                    </button>
                )}
                
                {/* Complete Button for In-Progress Missions */}
                {mission.status === 'in-progress' && onCompleteMission && (
                    <button
                        onClick={() => onCompleteMission(mission)}
                        className="w-full px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 text-sm font-medium mb-2"
                        title="Complete Mission"
                    >
                        <CheckCircle size={14} />
                        Complete Mission
                    </button>
                )}

                {/* Action Buttons Row - Compact height */}
                <div className="grid grid-cols-4 gap-1.5">
                    <button
                        onClick={onLoad}
                        className="w-full h-7 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors flex items-center justify-center"
                        title="Load Mission"
                    >
                        <Eye size={14} />
                    </button>
                    <button
                        onClick={onEdit}
                        className={`w-full h-7 ${themeClasses.buttonSecondary} rounded transition-colors flex items-center justify-center`}
                        title="Edit Mission"
                    >
                        <Edit3 size={14} />
                    </button>
                    <button
                        onClick={onExport}
                        className="w-full h-7 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center justify-center"
                        title="Export Mission"
                    >
                        <Download size={14} />
                    </button>
                    <button
                        onClick={onDelete}
                        className={`w-full h-7 ${themeClasses.buttonDanger} rounded transition-colors flex items-center justify-center`}
                        title="Delete Mission"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>

            {/* Route Preview */}
            <div className={`${themeClasses.secondary} rounded-lg p-3`}>
                <div className="flex items-center justify-between text-sm">
                    <span className={themeClasses.textMuted}>Route:</span>
                    <span className={themeClasses.textPrimary}>
                        {mission.locationData.startLocationName || 'Start'} → {mission.locationData.destinationLocationName || 'Destination'}
                    </span>
                </div>
            </div>
        </div>
    );
};

// Edit Mission Dialog Component
const EditMissionDialog = ({ mission, onSave, onCancel, themeClasses }) => {
    const { theme } = useTheme();
    const [name, setName] = useState(mission.missionInfo.name);
    const [notes, setNotes] = useState(mission.notes || '');
    const [status, setStatus] = useState(mission.status);
    const [tags, setTags] = useState(mission.tags?.join(', ') || '');

    const handleSave = () => {
        const updates = {
            name,
            notes,
            status,
            tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        };
        onSave(updates);
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} border ${themeClasses.border} rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl`}>
                <div className="flex items-center gap-3 mb-4">
                    <Edit3 className="text-blue-400" size={24} />
                    <h3 className={`${themeClasses.textPrimary} font-semibold`}>Edit Mission</h3>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Mission Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                        />
                    </div>

                    <div>
                        <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Status</label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} ${themeClasses.borderFocus} focus:outline-none`}
                        >
                            <option value="draft">Draft</option>
                            <option value="analyzed">Analyzed</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>

                    <div>
                        <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Tags (comma-separated)</label>
                        <input
                            type="text"
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="urgent, training, convoy"
                            className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
                        />
                    </div>

                    <div>
                        <label className={`block ${themeClasses.textSecondary} text-sm font-medium mb-2`}>Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className={`w-full px-3 py-2 ${themeClasses.input} border ${themeClasses.borderInput} rounded-lg ${themeClasses.textPrimary} placeholder-gray-500 ${themeClasses.borderFocus} focus:outline-none`}
                            placeholder="Add mission notes..."
                        />
                    </div>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={handleSave}
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                        Save Changes
                    </button>
                    <button
                        onClick={onCancel}
                        className={`flex-1 px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors`}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SavedMissions;
